import React, { FunctionComponent, useState } from 'react';
import classNames from 'classnames';
import { useForm, useField, splitFormProps } from 'react-form';

import styles from './Form.module.scss';
import { BRAND } from '../../constants/brand';
import { Button, Heading } from '../index';

interface FormProps {
  className?: string;
}

async function validateMessage(message, instance) {
  if (!message) {
    return 'A message is required';
  }

  return instance.debounce(async () => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    // Messages are valid, so return a false error
    return false;
  }, 500);
}

async function validateFrom(from, instance) {
  if (
    !from ||
    // eslint-disable-next-line no-control-regex
    !/(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/.test(
      from,
    )
  ) {
    return 'A valid email is required';
  }

  return instance.debounce(async () => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    // Froms are valid, so return a false error
    return false;
  }, 500);
}

const SelectField = props => {
  const [field, fieldOptions, { options, ...rest }] = splitFormProps(props);

  const {
    value = '',
    setValue,
    meta: { error, isTouched },
  } = useField(field, fieldOptions);

  const handleSelectChange = e => {
    setValue(e.target.value);
  };

  return (
    <div className={styles.selectContainer}>
      <select
        {...rest}
        value={value}
        onChange={handleSelectChange}
        className={styles.select}
        name="subject"
      >
        <option value="" selected disabled>
          Select a subject
        </option>
        {options.map(option => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      {isTouched && error ? <em>{error}</em> : null}
    </div>
  );
};

const MessageField = ({ id }) => {
  const {
    meta: { error, isTouched },
    getInputProps,
  } = useField('message', {
    validate: validateMessage,
  });

  return (
    <>
      <textarea
        className={styles.textarea}
        placeholder="Try and give us as much info as possible, times and dates that suit you as well as if you have any special requests."
        id={id}
        name="message"
        {...getInputProps()}
      />
      {isTouched && error ? <em>{error}</em> : null}
    </>
  );
};

const FromField = ({ id }) => {
  const {
    meta: { error, isTouched },
    getInputProps,
  } = useField('from', {
    validate: validateFrom,
  });

  return (
    <>
      <input
        className={styles.from}
        id={id}
        placeholder="Your email"
        name="from"
        {...getInputProps()}
      />
      {isTouched && error ? <em>{error}</em> : null}
    </>
  );
};

const SubscribeField = ({ id }) => {
  const { getInputProps, value, setValue } = useField('subscribe', {
    defaultValue: false,
  });

  const onChange = () => {
    setValue(value !== true);
  };

  return (
    <input
      className={classNames({
        [styles.subscribe]: true,
        [styles.subscribeIsChecked]: value === true,
      })}
      id={id}
      name="subscribe"
      type="checkbox"
      {...getInputProps({ onChange })}
    />
  );
};

const encode = data => {
  return Object.keys(data)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
    .join('&');
};

const ContactForm: FunctionComponent<FormProps> = ({ className }) => {
  const [submitted, setSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);

  const formClass = classNames(`${BRAND.SUPER_SHORT_NAME}-form`, className, {
    [styles.form]: true,
    [styles.submitted]: submitted,
  });

  async function sendToFakeServer(values) {
    await fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: encode({ 'form-name': 'contact', ...values }),
    })
      .then(() => {
        setSubmitted(true);
        setSubmissionError(null);
      })
      .catch(error => setSubmissionError(error));
  }

  // Use the useForm hook to create a form instance
  const {
    Form,
    meta: { isSubmitting, canSubmit },
  } = useForm({
    onSubmit: async values => {
      // onSubmit (and everything else in React Form)
      // has async support out-of-the-box
      await sendToFakeServer(values);
    },
    // debugForm: true,
  });

  const messageId = 'form-message';
  const fromId = 'from-id';
  const subscribeId = 'subscribe-id';
  const subjectId = 'subject-id';

  return submitted ? (
    <div className={formClass}>
      <Heading level={4}>
        Thank you for your message, we will get back to you as soon as we can!
      </Heading>
    </div>
  ) : (
    <Form
      name="contact"
      className={formClass}
      data-netlify="true"
      data-netlify-honeypot="bot-field"
    >
      {/* For Netlify */}
      <input type="hidden" name="form-name" value="contact" />

      <div className={styles.formGroup}>
        <div className={styles.label}>To</div>
        <div className={styles.input}>Lana@crystal-canine.co.uk</div>
      </div>

      <div className={styles.formGroup}>
        {/* eslint-disable-next-line max-len */}
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control,jsx-a11y/label-has-for */}
        <label className={styles.label} htmlFor={subjectId}>
          Subject
        </label>
        <SelectField
          id={subjectId}
          field="subject"
          options={[
            'Book an Appointment',
            'Training Enquiry',
            'Collaborations, Events & Guest Spots',
          ]}
          validate={value => (!value ? 'This is required!' : false)}
        />
      </div>

      <div className={styles.formGroup}>
        {/* eslint-disable-next-line max-len */}
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control,jsx-a11y/label-has-for */}
        <label className={styles.label} htmlFor={messageId}>
          Message
        </label>
        <MessageField id={messageId} />
      </div>

      <div className={styles.formGroup}>
        {/* eslint-disable-next-line max-len */}
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control,jsx-a11y/label-has-for */}
        <label className={styles.label} htmlFor={fromId}>
          From
        </label>
        <FromField id={fromId} />
      </div>

      <div
        className={classNames({
          [styles.formGroup]: true,
          [styles.subscribeFormGroup]: true,
        })}
      >
        {/* eslint-disable-next-line max-len */}
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control,jsx-a11y/label-has-for */}
        <label className={styles.subscribeLabel} htmlFor={subscribeId}>
          Subscribe to our mailing list?
        </label>
        <SubscribeField id={subscribeId} />
      </div>

      <div>
        <Button
          type="submit"
          disabled={!canSubmit}
          label="We will never share your details."
        >
          Send message
        </Button>
      </div>

      <div>
        <em>{isSubmitting ? 'Submitting...' : null}</em>
      </div>

      {submissionError && (
        <p>
          Oops! There was an error on our side. Please try again or feel free to
          email us directly at{' '}
          <a href="mailto:lana@crystalcanine.co.uk">lana@crystalcanine.co.uk</a>
          .
        </p>
      )}
    </Form>
  );
};

export default ContactForm;
